import { create } from 'zustand';
import type { ColumnAnalyzeRequest, Reinforcement, SectionType } from './types';

type Mode = 'design' | 'analysis';
type ViewKind = '2D' | '3D';

interface ColumnState {
  mode: Mode;
  view: ViewKind;
  /** Active request (mirrors the slot of the currently selected section type). */
  request: ColumnAnalyzeRequest;
  /** One independent saved request per section type. Switching section types
   *  saves the current edits into the previous slot and restores the saved
   *  values for the new slot (or defaults if that type has not been touched). */
  requests: Record<SectionType, ColumnAnalyzeRequest>;
  setMode: (m: Mode) => void;
  setView: (v: ViewKind) => void;
  setSectionType: (t: SectionType) => void;
  setSection: (patch: Partial<ColumnAnalyzeRequest['section']>) => void;
  setMaterials: (patch: Partial<ColumnAnalyzeRequest['materials']>) => void;
  setLoads: (patch: Partial<ColumnAnalyzeRequest['loads']>) => void;
  setReinforcement: (patch: Partial<Reinforcement>) => void;
  /** Replace the entire active request — used when loading a saved design. */
  loadRequest: (req: ColumnAnalyzeRequest) => void;
}

/** Tunable defaults per section type — each one starts from a sensible
 *  preset specific to that geometry so the user can switch types without
 *  having to re-enter everything from scratch. */
function defaultsFor(type: SectionType): ColumnAnalyzeRequest {
  const base: ColumnAnalyzeRequest = {
    section: { type, L1: 80, L2: 80, H_clear: 280, e1: 15, e2: 15 },
    materials: { fc: 280, fy: 4200, cover: 4 },
    reinforcement: {
      diam_corners: '3/4',
      n_intermediate_L1: 1,
      diam_intermediate_L1: '5/8',
      n_intermediate_L2: 1,
      diam_intermediate_L2: '5/8',
      stirrup_diam: '3/8',
      n_circular_bars: 8,
    },
    loads: { Pu: 80_000, Mu_x: 12_000, Mu_y: 8_000, Vu_x: 15_000, Vu_y: 12_000 },
    n_curve_points: 60,
  };
  // Per-type tweaks so the first thing the user sees on each tab is a viable
  // section (not a forced rectangular template):
  switch (type) {
    case 'rectangular':
      return { ...base, section: { ...base.section, L1: 30, L2: 40 } };
    case 'L':
      return { ...base, section: { ...base.section, L1: 80, L2: 80, e1: 15, e2: 15 } };
    case 'T':
      return { ...base, section: { ...base.section, L1: 80, L2: 60, e1: 15, e2: 15 } };
    case 'cruciforme':
      return { ...base, section: { ...base.section, L1: 80, L2: 80, e1: 20, e2: 20 } };
    case 'circular':
      return { ...base, section: { ...base.section, L1: 60, L2: 60 } };
    default:
      return base;
  }
}

const initialRequests: Record<SectionType, ColumnAnalyzeRequest> = {
  rectangular: defaultsFor('rectangular'),
  L: defaultsFor('L'),
  T: defaultsFor('T'),
  cruciforme: defaultsFor('cruciforme'),
  circular: defaultsFor('circular'),
};

export const useColumnStore = create<ColumnState>((set) => ({
  mode: 'design',
  view: '2D',
  requests: initialRequests,
  request: initialRequests.rectangular,
  setMode: (mode) => set({ mode }),
  setView: (view) => set({ view }),
  setSectionType: (type) =>
    set((s) => {
      // Save current edits into the previous type's slot, then load the new one.
      const prevType = s.request.section.type;
      const savedRequests = { ...s.requests, [prevType]: s.request };
      const nextRequest = savedRequests[type] ?? defaultsFor(type);
      // Defensive: enforce that the loaded slot has matching section.type
      // (the user might have stale data from older versions of the store).
      const fixed: ColumnAnalyzeRequest = {
        ...nextRequest,
        section: { ...nextRequest.section, type },
      };
      return { requests: savedRequests, request: fixed };
    }),
  setSection: (patch) =>
    set((s) => ({ request: { ...s.request, section: { ...s.request.section, ...patch } } })),
  setMaterials: (patch) =>
    set((s) => ({ request: { ...s.request, materials: { ...s.request.materials, ...patch } } })),
  setLoads: (patch) =>
    set((s) => ({ request: { ...s.request, loads: { ...s.request.loads, ...patch } } })),
  setReinforcement: (patch) =>
    set((s) => ({
      request: { ...s.request, reinforcement: { ...s.request.reinforcement, ...patch } },
    })),
  loadRequest: (req) =>
    set((s) => ({
      request: req,
      // Also update the slot for the loaded section type so switching tabs
      // and coming back doesn't blow away the loaded data.
      requests: { ...s.requests, [req.section.type]: req },
    })),
}));
